import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

// Define inline config interface to allow optional properties
export interface InlineConfig {
  tone_matching?: boolean;
  skip_on_factual?: boolean;
  insertion_density?: number;
  preferred_positions?: string[];
  blending_style?: 'conversational' | 'subtle' | 'distinct';
}

export interface AdPlacementConfig {
  mode: 'before' | 'after' | 'inline';
  inline_config?: InlineConfig;
  response_format?: 'text' | 'markdown' | 'html' | 'json' | 'auto';
}

export interface PublisherConfig {
  publisher_id: string;
  ad_placement_config: AdPlacementConfig;
  app_type?: string;
  is_active: boolean;
  
  // Add these for backward compatibility
  ad_placement_mode?: string;
  inline_config?: any;
  response_format_preference?: string;
}

// Database schema representation (matches actual DB)
interface DatabasePublisherConfig {
  id: number;
  publisher_id: string;
  ad_placement_mode: string;
  inline_config: any;
  response_format_preference: string;
  ad_categories: any;
  exclude_types: any;
  max_ads: number;
  debug_mode: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string | null;
  ad_placement_config?: any;
}

// Convert database schema to frontend schema
const convertDatabaseToFrontendConfig = (dbConfig: DatabasePublisherConfig): PublisherConfig => {
  // If ad_placement_config already exists, use it
  if (dbConfig.ad_placement_config) {
    return {
      publisher_id: dbConfig.publisher_id,
      ad_placement_config: dbConfig.ad_placement_config,
      is_active: dbConfig.is_active,
      // Include legacy fields for backward compatibility
      ad_placement_mode: dbConfig.ad_placement_mode,
      inline_config: dbConfig.inline_config,
      response_format_preference: dbConfig.response_format_preference
    };
  }
  
  // Otherwise, convert from old schema
  return {
    publisher_id: dbConfig.publisher_id,
    ad_placement_config: {
      mode: dbConfig.ad_placement_mode as 'before' | 'after' | 'inline',
      inline_config: dbConfig.inline_config || {
        tone_matching: true,
        skip_on_factual: true,
        insertion_density: 0.3,
        preferred_positions: ['paragraph_end', 'topic_transition'],
        blending_style: 'subtle'
      },
      response_format: dbConfig.response_format_preference as 'text' | 'markdown' | 'html' | 'json' | 'auto' || 'auto'
    },
    is_active: dbConfig.is_active,
    // Include legacy fields
    ad_placement_mode: dbConfig.ad_placement_mode,
    inline_config: dbConfig.inline_config,
    response_format_preference: dbConfig.response_format_preference
  };
};

// Convert frontend schema to database schema for updates
const convertFrontendToDatabaseConfig = (frontendConfig: Partial<PublisherConfig>): any => {
  const result: any = {
    // Initialize with required fields to ensure they are always included
    ad_placement_mode: "after",
    max_ads: 2,
    debug_mode: false
  };
  
  if (frontendConfig.is_active !== undefined) {
    result.is_active = frontendConfig.is_active;
  }
  
  if (frontendConfig.ad_placement_config) {
    const config = frontendConfig.ad_placement_config;
    
    // Map the new schema to the old schema expected by the backend
    if (config.mode) {
      result.ad_placement_mode = config.mode;
    }
    
    if (config.inline_config) {
      // Convert the new inline_config format to the expected backend format
      result.inline_config = {
        max_insertions: 1,
        insertion_strategy: "discourse",
        // Fix the blend_style mapping to match backend enum values
        blend_style: config.inline_config.blending_style === 'conversational' ? 'soft' : 
                     config.inline_config.blending_style === 'distinct' ? 'direct' : 
                     config.inline_config.blending_style === 'subtle' ? 'soft' : 'soft',
        insert_after_paragraphs: 1,
        tone_matching: config.inline_config.tone_matching !== undefined ? config.inline_config.tone_matching : false,
        skip_on_factual: config.inline_config.skip_on_factual !== undefined ? config.inline_config.skip_on_factual : false
      };
      
      // Map preferred positions if available
      if (config.inline_config.preferred_positions && config.inline_config.preferred_positions.length > 0) {
        // The backend doesn't have a field for preferred_positions, so we'll add it as a custom field
        result.inline_config.preferred_positions = config.inline_config.preferred_positions;
      }
      
      // Map insertion density if available
      if (config.inline_config.insertion_density !== undefined) {
        // The backend doesn't have a field for insertion_density, so we'll add it as a custom field
        result.inline_config.insertion_density = config.inline_config.insertion_density;
      }
    }
    
    if (config.response_format) {
      // Map the frontend format values to backend format values
      const formatMap: Record<string, string> = {
        'text': 'plain',
        'markdown': 'markdown',
        'html': 'html',
        'json': 'json',
        'auto': 'auto'
      };
      result.response_format_preference = formatMap[config.response_format] || 'plain';
    }
  }
  
  return result;
};

// Create a default config with all required fields
const DEFAULT_CONFIG: PublisherConfig = {
  publisher_id: '',
  ad_placement_config: {
    mode: 'after',
    inline_config: {
      tone_matching: true,
      skip_on_factual: true,
      insertion_density: 0.3,
      preferred_positions: ['paragraph_end', 'topic_transition'],
      blending_style: 'subtle'
    },
    response_format: 'auto'
  },
  is_active: true,
  
  // Include legacy fields for backward compatibility
  ad_placement_mode: 'after',
  response_format_preference: 'auto',
  inline_config: {
    max_insertions: 1,
    insertion_strategy: 'discourse',
    blend_style: 'soft',
    insert_after_paragraphs: 1,
    tone_matching: true,
    skip_on_factual: true
  }
};

const publisherConfigService = {
  // Get publisher configuration
  getPublisherConfig: async (publisherId: string): Promise<PublisherConfig> => {
    console.log(`Fetching publisher config for ID: ${publisherId}`);
    console.log(`Using API URL: ${API_URL}`);
    
    try {
      console.log(`Making request to: ${API_URL}/publisher-configs/publisher/${publisherId}`);
      const response = await axios.get(`${API_URL}/publisher-configs/publisher/${publisherId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      console.log('Publisher config API response:', response.data);
      
      // Check if the response data has the expected structure
      if (!response.data.ad_placement_config && response.data.ad_placement_mode) {
        console.log('Response has database format, converting to frontend format');
        return convertDatabaseToFrontendConfig(response.data);
      }
      
      return response.data;
    } catch (error) {
      console.error('Error fetching publisher config:', error);
      if (axios.isAxiosError(error)) {
        console.error('Axios error details:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          headers: error.response?.headers
        });
      }
      
      // Return default config with the publisher ID
      return {
        ...DEFAULT_CONFIG,
        publisher_id: publisherId
      };
    }
  },

  // Update publisher configuration
  updatePublisherConfig: async (publisherId: string, config: Partial<PublisherConfig>): Promise<PublisherConfig> => {
    try {
      console.log(`Updating publisher config for ID: ${publisherId}`, config);
      
      // Convert frontend config format to database format
      const dbConfig = convertFrontendToDatabaseConfig(config);
      console.log('Converted to database format for update:', dbConfig);
      
      const response = await axios.put(`${API_URL}/publisher-configs/publisher/${publisherId}`, dbConfig, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      console.log('Update response:', response.data);
      
      // Convert the response back to frontend format
      return convertDatabaseToFrontendConfig(response.data);
    } catch (error) {
      console.error('Error updating publisher config:', error);
      throw error;
    }
  },

  // Get default configurations by app type
  getDefaultConfig: async (appType: string): Promise<AdPlacementConfig> => {
    try {
      const response = await axios.get(`${API_URL}/defaults/app/${appType}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching default config:', error);
      
      return {
        mode: 'after',
        inline_config: {
          tone_matching: true,
          skip_on_factual: true,
          insertion_density: 0.3,
          preferred_positions: ['paragraph_end', 'topic_transition'],
          blending_style: 'subtle'
        },
        response_format: 'auto'
      };
    }
  },

  // Get performance metrics for different placement strategies
  getPlacementMetrics: async (publisherId: string, timeframe: string = '30days'): Promise<any> => {
    try {
      const response = await axios.get(`${API_URL}/metrics/placement/${publisherId}?timeframe=${timeframe}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching placement metrics:', error);
      
      return {
        ctr: {
          before: [1.2, 1.5, 1.8, 1.6, 1.9],
          after: [1.0, 1.1, 1.3, 1.4, 1.6],
          inline: [2.1, 2.3, 2.5, 2.8, 3.0],
          labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4', 'Week 5']
        },
        impressions: {
          before: [120, 150, 180, 160, 190],
          after: [100, 110, 130, 140, 160],
          inline: [210, 230, 250, 280, 300],
          labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4', 'Week 5']
        },
        clicks: {
          before: [15, 18, 20, 19, 25],
          after: [12, 14, 16, 18, 22],
          inline: [30, 33, 38, 42, 45],
          labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4', 'Week 5']
        },
        revenue: {
          before: [5.2, 6.1, 7.3, 6.9, 8.4],
          after: [4.5, 5.0, 5.8, 6.2, 7.1],
          inline: [9.8, 10.5, 11.2, 12.8, 14.5],
          labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4', 'Week 5']
        },
        engagement: {
          before: [25, 28, 30, 29, 32],
          after: [22, 24, 26, 27, 29],
          inline: [35, 38, 41, 44, 48],
          labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4', 'Week 5']
        },
        timeframe: timeframe
      };
    }
  }
};

export default publisherConfigService; 